import AsyncStorage from '@react-native-async-storage/async-storage';
import Axios from 'axios';
import React, { useState, useRef } from 'react';
import { useEffect } from 'react';
import { TouchableOpacity } from 'react-native';
import { View, StyleSheet, Picker, Text } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { GetBothSettings } from '../../actions/userAction';
import { Entypo } from '@expo/vector-icons';
import styles from '../../style/orderlist';
import { getSelectedOrderIds, getSelectedOrders } from '../../helpers/scanPackFunctions';
import ReRunRulesModal from './ReRunRulesModal';
import BoxChampCalculateModal from './BoxChampCalculateModal';
import { MAX_ORDERS_PER_REQUEST, DRY_RUN_THRESHOLD } from '../../helpers/rulesConfig';

import useDropdown from './useDropdown';

// GROOV-5067 — bounded fallback refresh for queued bulk actions.
//
// The websocket stays the PRIMARY refresh trigger (GROOV-4901 deliberately removed
// the immediate post-action reload, because these actions are queued server-side and
// an immediate reload just re-renders the pre-action rows). The terminal
// 'groove_bulk_actions' pnotif is what tells the grid the rows really changed.
//
// The failure this guards against is that message never arriving: it can be dropped,
// delayed, or lost to a socket disconnect. When that happens the grid keeps showing
// the old User / Status / Tote for rows that were in fact cleared, and the user
// concludes the action failed when it succeeded.
//
// 6000ms: a healthy socket delivers scheduled -> in_progress -> completed for a
// normal-sized batch within a second or two, so this sits comfortably past the happy
// path and the common case still refreshes via the socket with no extra request. It
// is also well inside the window where a user would start assuming nothing happened.
// Firing it early on a slow-but-alive job is harmless — the fallback is a plain grid
// reload, so the worst case is the grid showing accurate mid-job server state, and
// the socket's own refresh still lands later.
export const BULK_ACTION_REFRESH_FALLBACK_MS = 6000;

// Actions that change the User / Status / Tote a grid row displays. Exports and
// print jobs do not mutate rows, so they need no refresh at all.
export const ROW_MUTATING_BULK_ACTIONS = ['delete', 'duplicate', 'clear_tote_user'];

// The completion payload's `activity` field is the backend job name, which for
// this one action doesn't match the frontend's action id.
const BULK_ACTION_ACTIVITY_NAMES = {clear_tote_user: 'clear_tote_and_user'};

const CustomDropdown = ({ options, onSelect, title, isOpen, toggleDropdown, handleOutsideClick, testID }) => {

  const { dropdownRef, handleSelectOption } = useDropdown(onSelect, isOpen, toggleDropdown);


  return (
    <View style={styles.dropdownContainer} ref={dropdownRef}>
      <TouchableOpacity testID={testID} onPress={toggleDropdown} style={styles.selectedOption}>
        <View style={{ flexDirection: 'row', alignItems: 'center', borderBottom: "1px solid #ddd" }}>
          <Text style={{ fontFamily: 'Poppins_600SemiBold', color: 'white' }}>{title}</Text>
          <Entypo name="chevron-down" size={18} color="white" style={{ marginLeft: 3 }} />
        </View>
      </TouchableOpacity>
      {isOpen && (
        <View style={styles.optionsStatusContainer}>
          {options.map((option, index) => (
            <TouchableOpacity
              testID={index}
              key={index}
              style={style.option}
              onPress={() => handleSelectOption(option)}>
              <Text style={{color: 'white'}}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

function UpdateOrdersDropDowns  ({ updateOrderStatus, gridSelectionRef, skip, limit, statuses, redirectToProductDetail, alertBox, getOrderList, searchString, PresentFilterValues, resetSelectedOrders, getUnselectedItems, unselectedOrderItems, selectedRows, allOrdersSelected, dateValue, dateRange, selectedUsers, selectedTags, selectedSkus, matchOption, filterIncludedTags, filterIncludedSkus, refreshFallbackMs = BULK_ACTION_REFRESH_FALLBACK_MS }) {
  let url= '';
  const dispatch = useDispatch();
  const {bothSettings} = useSelector(state => state.user);
  const general_settings = bothSettings?.data?.data?.general_setting;
  const isBoxChamp = general_settings?.is_box_champ === true;

  useEffect(() => {
    if (!general_settings) {
      dispatch(GetBothSettings());
    }
  }, []);
  const orders = gridSelectionRef?.selected
  let gridSelectionIdsArray = getSelectedOrderIds(selectedRows)

  const orders_id = gridSelectionIdsArray

  const dropdownOptions = {
    changeStatus: [
      { label: 'Awaiting', value: 'awaiting' },
      { label: 'Scanned', value: 'scanned' },
      { label: 'Service Issue', value: 'serviceissue' },
      { label: 'Cancelled', value: 'cancelled' },
    ],
    editAndExport: [
      { label: 'Delete', value: 'delete' },
      { label: 'Duplicate', value: 'duplicate' },
      { label: 'Clear Tote & User', value: 'clear_tote_user' },
      { label: 'Export Orders (Standard)', value: 'items_list_standard_export' },
      { label: 'Export Consolidated SKU list', value: 'items_list_by_sku' },
      ...(isBoxChamp ? [{ label: 'Calculate Boxes', value: 'calculate_boxes' }] : []),
    ],
    print: [
      { label: 'Shipping Label', value: 'shipping_label' },
      { label: 'Packing Slip', value: 'packing_slip' },
      { label: 'Pick List', value: 'pick_list' },
      { label: 'Pick List & Packing Slips', value: 'pick_list_packing_slips' },
      { label: 'Print Barcode', value: 'barcode_slip' },
    ],
  };


  const [openDropdown, setOpenDropdown] = useState(null);
  const [rerunModalVisible, setRerunModalVisible] = useState(false);
  const [rerunOrderIds, setRerunOrderIds] = useState([]);
  const [skipDryRun, setSkipDryRun] = useState(false);
  const [boxChampModalVisible, setBoxChampModalVisible] = useState(false);
  const [boxChampOrderIds, setBoxChampOrderIds] = useState([]);

  // GROOV-5067 defect 2: ordersUpdatedSeq bumps for ANY of four unrelated sources
  // (a different bulk action, a tag action, a packing-slip print, disconnect
  // recovery), so watching it as a single counter let an unrelated bump cancel
  // THIS action's fallback before this action's own completion had actually
  // landed. ordersUpdatedActivitySeqs (per "<identifier>:<activity>" key) fixes
  // that, and — unlike the raw `ordersUpdated` flag — it survives the grid's own
  // consume-and-reset, so it can be read once when the fallback timer fires
  // instead of needing to be observed on every intermediate render.
  const ordersActivitySeqs = useSelector(state => state.order?.ordersUpdatedActivitySeqs || {});
  const ordersActivitySeqsRef = useRef(ordersActivitySeqs);
  ordersActivitySeqsRef.current = ordersActivitySeqs;
  const fallbackRefreshTimerRef = useRef(null);

  const clearFallbackRefresh = () => {
    if (fallbackRefreshTimerRef.current) {
      clearTimeout(fallbackRefreshTimerRef.current);
      fallbackRefreshTimerRef.current = null;
    }
  };

  useEffect(() => clearFallbackRefresh, []);

  const scheduleGridRefreshFallback = action => {
    if (typeof getOrderList !== 'function') return;
    clearFallbackRefresh();
    const activityKey = `orders:${BULK_ACTION_ACTIVITY_NAMES[action] || action}`;
    const seqBeforeAction = ordersActivitySeqsRef.current[activityKey] || 0;
    fallbackRefreshTimerRef.current = setTimeout(() => {
      fallbackRefreshTimerRef.current = null;
      const seqAfterAction = ordersActivitySeqsRef.current[activityKey] || 0;
      if (seqAfterAction > seqBeforeAction) return;
      getOrderList();
    }, refreshFallbackMs);
  };

  // Get user data to check rules_manager permission and tenant feature flag
  const userData = useSelector(state => state.user.userInfo);
  const isRulesManager = userData?.role?.rules_manager === true;
  const isRulesEngineEnabled = userData?.rules_engine_enabled === true;
  const canReRunRules = isRulesManager && isRulesEngineEnabled;
  const tenantId = userData?.tenant_name || '';

  const handleReRunRules = () => {
    const selected = getSelectedOrderIds(selectedRows);
    const ids = selected.map(item => item.id).filter(Boolean);

    if (ids.length === 0) {
      alertBox('Select at least one order');
      return;
    }
    if (ids.length > MAX_ORDERS_PER_REQUEST) {
      alertBox(`Maximum ${MAX_ORDERS_PER_REQUEST} orders per request`);
      return;
    }

    setRerunOrderIds(ids);
    setSkipDryRun(ids.length > DRY_RUN_THRESHOLD);
    setRerunModalVisible(true);
  };

  const handleRerunFailed = (failedIds) => {
    if (failedIds.length === 0) return;
    if (failedIds.length > MAX_ORDERS_PER_REQUEST) {
      alertBox(`Maximum ${MAX_ORDERS_PER_REQUEST} orders per request`);
      return;
    }
    setRerunOrderIds(failedIds);
    setSkipDryRun(failedIds.length > DRY_RUN_THRESHOLD);
    setRerunModalVisible(true);
  };

  const handlePrintLabel = async () => {
    const userUrl = await AsyncStorage.getItem('url');
    if (gridSelectionRef.selected !== true && getSelectedOrders(selectedRows).length === 1) {
      const selected_order = orders_id[0];
      try {
        const url = (userUrl + '/orders/' + selected_order.id + '/print_shipping_label.json');
        const access_token = await AsyncStorage.getItem('access_token');
        const data = await Axios({
          method: 'get',
          url: url,
          headers: {Authorization: `Bearer ${access_token}`},
        });

        const error = data.data.error;

        if (data.status && error === "") {
          window.open(data.data.url);
        } else {
          if (error) {
            alertBox(error);
            console.error('Error:', error);
          }
          if (data.data.ss_label_order_data) {
            // debugger
            let current_order_item = Object.values(orders)[0].currentItem
            // const item = orders.find(item => cellProps.data.currentItem.order_info?.id === item.order_info?.id);
            redirectToProductDetail(current_order_item);
          }
        }
      } catch (error) {
        alertBox(error);
      }
    } else {
      alertBox('Please select a single order');
    }
  };

  const generateList = async(action) => {
    const userUrl = await AsyncStorage.getItem('url');
    const statusFilter = statuses.map(str => str?.toLowerCase()).join(',') || 'all';
    let data = {
      filter: statusFilter,
      filters: PresentFilterValues,
      search: searchString,
      username: (selectedUsers || []).filter(Boolean).join(','),
      tags_name: (selectedTags || []).filter(Boolean).join(','),
      filterIncludedTags: filterIncludedTags,
      skus_name: selectedSkus || [],
      filterIncludedSkus: filterIncludedSkus,
      match_option: matchOption,
      order: 'DESC',
      limit: limit,
      offset: skip,
      app: 'app',
      count: 0,
      orderArray: orders_id,
      select_all: false,
      unselected: unselectedOrderItems || "",
      dateValue: dateValue,
      dateRange: JSON.stringify(dateRange)
    }

    if(allOrdersSelected === true){
        data.select_all = true
      }

      if (action === "pick_list") {
        url = (`${userUrl}/orders/generate_pick_list.json`);
      } else if (action === "packing_slip") {
        // if (gridSelectionRef.selected === true) {
        //   let slipUrl = '';
        //   let lowerCaseStatuses = statuses.map(str => str?.toLowerCase());
        //   slipUrl = `${userUrl}/orders/generate_all_packing_slip?filter=${lowerCaseStatuses || 'all'}&sort=${""}&order=${'DESC'}`;
        //   window.open(slipUrl);
        // } else {
          url = (`${userUrl}/orders/generate_packing_slip.json`);
        // }
      } else if (action === 'items_list_standard_export') {
        data.filters = PresentFilterValues;
        let lowerCaseStatuses = statuses.map(str => str?.toLowerCase()).join(',');
        data.filter = lowerCaseStatuses || 'all';
        data.search = searchString;
        const product_search_toggle = general_settings.search_by_product
        url = (`${userUrl}/orders/order_items_export.json?product_search_toggle=${product_search_toggle}&export_type=${"standard_order_export"}`);
      } else if (action === 'items_list_by_sku') {
        data.filters = PresentFilterValues;
        let lowerCaseStatuses = statuses.map(str => str?.toLowerCase()).join(',');
        data.filter = lowerCaseStatuses || 'all';
        data.search = searchString;
        const product_search_toggle = general_settings.search_by_product
        url = (`${userUrl}/orders/order_items_export.json?product_search_toggle=${product_search_toggle}&export_type=${"by_sku"}`);
      } else if (action === 'barcode_slip') {
        let barcodeUrl = null;
        if (gridSelectionRef.selected === true) {
          url = (`${userUrl}/products/bulk_barcode_generation?ids=all&status=${statusFilter}`);
        } else {
          let ids = "";
          orders_id.forEach(item => {
            ids = ids + "," + item.id;
          });

          if (orders.length > 20) {
            url = (`${userUrl}/products/bulk_barcode_generation?ids=${ids}`);
          } else {
            barcodeUrl = `${userUrl}/products/bulk_barcode_pdf.pdf?ids=${ids}`;
          }
        }
        if (barcodeUrl, action) {
          window.open(barcodeUrl);
        }
      }
      else if (action == "delete") {
        data.status = "";
        data.filters = PresentFilterValues;
        let lowerCaseStatuses = statuses.map(str => str?.toLowerCase()).join(',');
        data.filter = lowerCaseStatuses || 'all';
        data.search = searchString;
        url = userUrl + '/orders/delete_orders.json';
      } else if (action == "clear_tote_user") {
        data.status = "";
        data.filters = PresentFilterValues;
        let lowerCaseStatuses = statuses.map(str => str?.toLowerCase()).join(',');
        data.filter = lowerCaseStatuses || 'all';
        data.search = searchString;
        url = userUrl + '/orders/clear_tote_and_user.json';
      } else if (action == "duplicate") {
        data.status = "";
        data.filters = PresentFilterValues;
        let lowerCaseStatuses = statuses.map(str => str?.toLowerCase()).join(',');
        data.filter = lowerCaseStatuses || 'all';
        // data.select_all = false
        data.search = searchString
        url = userUrl + '/orders/duplicate_orders.json';
      }
      postData(url, action, data)
    };

  const postData = async(url, action, data) => {
    const access_token = await AsyncStorage.getItem('access_token');
    if (url !== "") {
      try {
        const response = await Axios({
          method: 'post',
          url: url,
          headers: {Authorization: `Bearer ${access_token}`},
          data
        });
        if (action === "pick_list") {
          window.open(response.data.url);
        } else if (action === 'items_list_standard_export' ||  action === 'items_list_by_sku') {
          if (response.status && response.data.filename !== '') {
            window.open(response.data.filename);
          } else {
            console.log(response.data.messages);
          }
        }
        if (action === 'barcode_slip') {
          console.log(response.data.messages);
        }
        resetSelectedOrders();
        if (ROW_MUTATING_BULK_ACTIONS.includes(action)) {
          scheduleGridRefreshFallback(action);
        }
      } catch (error) {
        alertBox(error);
      }
    }
  }

  const handleCalculateBoxes = () => {
    const selected = getSelectedOrderIds(selectedRows);
    const ids = selected.map(item => item.id).filter(Boolean);
    if (ids.length === 0) {
      alertBox('Select at least one order');
      return;
    }
    if (ids.length > MAX_ORDERS_PER_REQUEST) {
      alertBox(`Maximum ${MAX_ORDERS_PER_REQUEST} orders per request`);
      return;
    }
    setBoxChampOrderIds(ids);
    setBoxChampModalVisible(true);
  };

  const handleGenerate = (value) => {
    if (value === 'rerun_rules') {
      handleReRunRules();
    } else if (value === 'pick_list_packing_slips') {
      generate_orders_pick_list_and_packing_slip();
    } else if (value === 'shipping_label') {
      handlePrintLabel();
    } else if (value === 'calculate_boxes') {
      handleCalculateBoxes();
    } else {
      generateList(value);
    }
  };

  const handleValueChange = (itemValue) => {
    updateOrderStatus(itemValue);
  };

  const generate_orders_pick_list_and_packing_slip = () => {
    generateList("pick_list");
    generateList("packing_slip");
  };

  const toggleDropdown = (dropdownKey) => {
    setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
  };



  const handleOutsideClick = () => {
    setOpenDropdown(null)
  };

  const renderDropdown = (options, functionCall, title, isOpen, toggleDropdown, testID) => (
    <CustomDropdown
      options={options}
      onSelect={functionCall}
      title={title}
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      handleOutsideClick={handleOutsideClick}
      testID={testID}
    />
  );

  return (
    <View style={styles.container}>
      {renderDropdown(dropdownOptions.changeStatus, (value) => handleValueChange(value), "Change Status", openDropdown === 'dropdown1', () => toggleDropdown('dropdown1'), 'ChangeStatusBtn')}
      {renderDropdown([...dropdownOptions.editAndExport, ...(canReRunRules ? [{ label: 'Re-Run Rules', value: 'rerun_rules' }] : [])], (value) => handleGenerate(value), "Edit & Export", openDropdown === 'dropdown2', () => toggleDropdown('dropdown2'), 'Edit&Export')}
      {renderDropdown(dropdownOptions.print, (value) => {handleGenerate(value)}, "Print", openDropdown === 'dropdown3', () => toggleDropdown('dropdown3'), 'Print')}
      <ReRunRulesModal
        visible={rerunModalVisible}
        onClose={() => setRerunModalVisible(false)}
        orderIds={rerunOrderIds}
        tenantId={tenantId}
        onRerunFailed={handleRerunFailed}
        alertBox={alertBox}
        skipDryRun={skipDryRun}
      />
      <BoxChampCalculateModal
        visible={boxChampModalVisible}
        onClose={() => setBoxChampModalVisible(false)}
        orderIds={boxChampOrderIds}
        alertBox={alertBox}
      />
    </View>
  );
};


const style = StyleSheet.create({
  option: {
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    color: 'white',
    border: 'none'
  },
});

export default UpdateOrdersDropDowns;
